import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel } from "swiper/modules";
import "swiper/css";
import axiosInstance from "../api/axiosInstance";
import { SellRounded, Star, StarBorder, BookmarkBorderRounded, BookmarkRounded, IosShare, Place, DepartureBoard } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { CircularProgress } from '@mui/material';
import Comments from "./Comments";
import { isVideoFile } from '../utils/mediaUtils';
import '../styles/trippin.css';

const PlaceView = () => {
  const { placeId } = useParams();
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPlaceDetails = async () => {
    setLoading(true);
    try {
      const resp = await axiosInstance.get(`/saved/places/${placeId}`);
      const fetchedPlace = resp.data.place;

      if (!fetchedPlace.photos) {
        fetchedPlace.photos = [];
      }
  
      setPlace(fetchedPlace);
    } catch (error) {
      console.error("Error fetching place details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlaceDetails();
  }, [placeId]);

  if (loading) {
    return (
      <div className="loading-container">
        <CircularProgress size={40} sx={{ color: '#d1d5db' }} />
      </div>
    );
  }

  if (!place) {
    return (
      <div className="loading-container" style={{ height: "unset" }}>
        <p style={{ fontSize: "18px", color: "#d1d5db", textAlign: "center", marginTop: "50px" }}>
          No saved places found        
        </p>
      </div>
    );
  }

  return (
    <div style={{ width: "100vw", height: "100vh" }}>
      <SinglePlaceView place={place} />
    </div>
  );
};

export default PlaceView;

const SinglePlaceView = ({ place }) => {
  const verticalSwiperRef = useRef(null);

  const [isSaved, setIsSaved] = useState(true);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);

  //const SWIPE_THRESHOLD = 50;
  //const [startY, setStartY] = useState(null);
/*
  const handleTouchStart = (e) => {
    setStartY(e.touches[0].clientY);
  };

  const handleTouchEnd = (e) => {
    if (startY === null) return;
    const endY = e.changedTouches[0].clientY;
    const deltaY = startY - endY;
    if (!outerSwiper) return;

    if (deltaY > SWIPE_THRESHOLD) {
      outerSwiper.slideNext();
    }
    else if (deltaY < -SWIPE_THRESHOLD) {
      outerSwiper.slidePrev();
    }
    setStartY(null);
  };
*/
  const toggleSavePlace = async () => {
    try {
      if (isSaved) {
        await axiosInstance.delete(`/trippins/places/${place.places[0].place_id}/save`, {
          data: { post_place_id: place.places[0].post_place_id },
        });
        setIsSaved(false);
      }
      else {
        await axiosInstance.post(`/trippins/places/${place.places[0].place_id}/save`, {
          post_place_id: place.places[0].post_place_id,
        });
        setIsSaved(true);
      }
    } catch (error) {
      console.error("Error toggling place save:", error);
    }
  };

  const sharePlace = async () => {
    try {
      await axiosInstance.post(`/trippins/places/${place.place_id}/share`);
      alert("Place shared!");
    } catch (error) {
      console.error("Error sharing place:", error);
    }
  };

  const openInMaps = () => {
    if (place.latitude && place.longitude) {
      const url = `https://www.google.com/maps?q=${place.latitude},${place.longitude}`;
      window.open(url, "_blank");
    } else {
      alert("No lat/lng available");
    }
  };

  const openComments = () => {
    setIsCommentsOpen(true);
  };

  const closeComments = () => {
    setIsCommentsOpen(false);
  };

  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const images = Array.isArray(place.photos) && place.photos.length > 0
  ? place.photos.map(photo => photo.content_url)
  : ["/defaultImage.jpg"];

  return (
    <Swiper
      ref={verticalSwiperRef}
      direction="vertical"
      modules={[Mousewheel]}
      mousewheel={false}
      nested={true}
      style={{ width: "100%", height: "100%" }}
    >
      <SwiperSlide>
        <div className="place-slide">
          
          <div className="place-header-container">
            <div className="place-header is-profile">
              {/* Profile Info */}
              <div className="profile-container">
                <img
                  src={place.places[0].profile_picture || "/assets/images/default_profile.png"}
                  alt="User Profile"
                  className="profile-picture"
                />
                <div className="profile-text">
                  <span className="profile-username">{place.places[0].username}</span>
                </div>
              </div>

              {/* Flag */}
              <div className="flags-container">
                <img
                  key={place.country}
                  src={`/assets/flags/${place.places[0].country.toLowerCase()}.svg`}
                  alt={place.country}
                  className="flag-image"
                />
              </div>
            </div>
          </div>

          <div className="image-action-icons">
            <IconButton
              onClick={toggleSavePlace}
              size="large"
              style={{ color: "#fff" }}
            >
              {isSaved ? (
                <BookmarkRounded
                  style={{
                    color: "#2c3e50",
                    width: "33px",
                    height: "33px",
                    filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.4))",
                  }}
                />
              ) : (
                <BookmarkBorderRounded
                  style={{
                    width: "33px",
                    height: "33px",
                    filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.4))",
                  }}
                />
              )}
            </IconButton>
            
            <IconButton
              onClick={openComments}
              size="large"
              className="custom-icon-button"
            >
              <img src="/assets/icons/comments.svg" alt="Comments" className="custom-icon" />
            </IconButton>

            <IconButton
              onClick={sharePlace}
              size="large"
              style={{ color: "#fff" }}
            >
              <IosShare
                style={{
                  width: "33px",
                  height: "33px",
                  filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.4))",
                }}
              />
            </IconButton>

            <IconButton
              onClick={openInMaps}
              size="large"
              style={{ color: "#fff" }}
            >
              <Place
                style={{
                  width: "33px",
                  height: "33px",
                  filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.4))",
                }}
              />
            </IconButton>
          </div>

          {isCommentsOpen && (
            <div className="comments-overlay">
              <Comments
                trippinPostId={place.trippin_post_id}
                placeName={place.place_name}
                id={place.places[0].post_place_id}
                commentType="trippin"
                onClose={closeComments}
              />
            </div>
          )}

          <div className="horizontal-swiper-container">
            <Swiper
              direction="horizontal"
              modules={[Mousewheel]}
              mousewheel={false}
              nested={true}
            >
              {images.length > 0 ? (
                images.map((imgUrl, index) => {
                  const isVideo = isVideoFile(imgUrl);
                  return (
                    <SwiperSlide key={index}>
                      {isVideo ? (
                        <video
                          src={imgUrl}
                          className="place-image"
                          autoPlay
                          loop
                          playsInline
                        />
                      ) : (
                        <img
                          src={imgUrl}
                          alt={`PlaceImage-${index}`}
                          className="place-image"
                        />
                      )}
                    </SwiperSlide>
                  );
                })
              ) : (
                <SwiperSlide>
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      background: "#ccc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    No images
                  </div>
                </SwiperSlide>
              )}
            </Swiper>
          </div>

          <div className="caption-area">

            <h2 className="place-title">{place.place_name}</h2>
            {place.places[0].caption && <p className="caption">{place.places[0].caption}</p>}
       
            {place.places[0].review && (
              <div className="review-rating">
                {Array.from({ length: 5 }).map((_, index) =>
                  index < place.places[0].review ? (
                    <Star key={index} style={{ color: "#FFD700" }} />
                  ) : (
                    <StarBorder key={index} style={{ color: "#FFD700" }} />
                  )
                )}
              </div>
            )}

            {place.places[0].price && (
              <p className="price">
                <SellRounded className="money-icon" />
                <span>${place.places[0].price}</span>
              </p>
            )}

            {place.places[0].companionship && (
              <p className="companionship">
                <strong>Companionship:</strong> {capitalize(place.places[0].companionship)}
              </p>
            )}
          </div>
        </div>
      </SwiperSlide>
    </Swiper>
  );
};
